const COORD_TOLERANCE = 0.5;

export function extractPunchListDrawingLinks(annotations, drawingId) {
  if (!drawingId || !Array.isArray(annotations)) {
    return [];
  }

  const links = [];
  for (const ann of annotations) {
    if (ann?.type !== "connection") continue;
    const tag = ann.rfiTag;
    if (!tag || tag.type !== "punchlist") continue;

    const pos = tag.tagPosition;
    if (!tag.punchListId || !tag.rfiId || pos?.x == null || pos?.y == null) {
      continue;
    }

    links.push({
      punchListId: String(tag.punchListId),
      punchListItemId: String(tag.rfiId),
      drawingId: String(drawingId),
      x: pos.x,
      y: pos.y,
    });
  }
  return links;
}

function coordsMatch(a, b) {
  return Math.abs(Number(a) - Number(b)) <= COORD_TOLERANCE;
}

function linksMatch(a, b) {
  return (
    a.punchListId === b.punchListId &&
    a.punchListItemId === b.punchListItemId &&
    a.drawingId === b.drawingId &&
    coordsMatch(a.x, b.x) &&
    coordsMatch(a.y, b.y)
  );
}

export function findRemovedPunchListDrawingLinks(
  previousAnnotations,
  newAnnotations,
  drawingId,
) {
  const previousLinks = extractPunchListDrawingLinks(
    previousAnnotations,
    drawingId,
  );
  const newLinks = extractPunchListDrawingLinks(newAnnotations, drawingId);

  return previousLinks.filter(
    (prev) => !newLinks.some((current) => linksMatch(prev, current)),
  );
}

export function findAddedPunchListDrawingLinks(
  previousAnnotations,
  newAnnotations,
  drawingId,
) {
  const previousLinks = extractPunchListDrawingLinks(
    previousAnnotations,
    drawingId,
  );
  const newLinks = extractPunchListDrawingLinks(newAnnotations, drawingId);

  return newLinks.filter(
    (current) => !previousLinks.some((prev) => linksMatch(prev, current)),
  );
}

export async function syncPunchListDrawingLinksOnSave(
  previousAnnotations,
  newAnnotations,
  drawingId,
  { linkFn, unlinkFn },
) {
  const removed = findRemovedPunchListDrawingLinks(
    previousAnnotations,
    newAnnotations,
    drawingId,
  );
  const added = findAddedPunchListDrawingLinks(
    previousAnnotations,
    newAnnotations,
    drawingId,
  );

  if (!removed.length && !added.length) {
    return;
  }

  const unlinkResults = await Promise.allSettled(
    removed.map((link) =>
      unlinkFn(
        link.punchListId,
        link.punchListItemId,
        link.drawingId,
        link.x,
        link.y,
      ),
    ),
  );
  const linkResults = await Promise.allSettled(
    added.map((link) =>
      linkFn(
        link.punchListId,
        link.punchListItemId,
        link.drawingId,
        link.x,
        link.y,
      ),
    ),
  );

  const failed = [...unlinkResults, ...linkResults].filter(
    (r) => r.status === "rejected",
  );
  if (failed.length) {
    throw new Error(
      `Failed to sync ${failed.length} punch list drawing link(s).`,
    );
  }
}

/** @deprecated Use syncPunchListDrawingLinksOnSave instead. */
export async function syncRemovedPunchListDrawingLinks(
  previousAnnotations,
  newAnnotations,
  drawingId,
  unlinkFn,
) {
  return syncPunchListDrawingLinksOnSave(previousAnnotations, newAnnotations, drawingId, {
    linkFn: async () => {},
    unlinkFn,
  });
}
